import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import LoadingSpinner from '../components/LoadingSpinner'
import { ArrowRight, MessageCircle } from 'lucide-react'

const HERO_IMAGE = '/Gallery/Academy/Creative Training Sessions/268A5756.JPG'
const CONTACT_EMAIL = 'synergymultimediaacademy@gmail.com'
const WHATSAPP_NUMBER = '256706739311'

const whatsappLink = (message) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`

// Card images by discipline (projects have no image field yet).
const DISCIPLINE_IMAGES = {
  Photography: '/Gallery/Academy/Future Storytellers/Kelly  (1).jpeg',
  'AI / Creative': '/Gallery/ai-generated/AI Color Grading.png',
  Video: '/Gallery/Academy/Workshops/ww.jpg',
  Design: '/Gallery/Academy/showcase/thinking.jpg',
}
const DEFAULT_IMAGE = '/Gallery/Academy/Creative Training Sessions/DSC02423.jpg'

const FACTS = [
  { value: '5–6 people', label: 'Small cohorts, close mentoring' },
  { value: '2–6 weeks', label: 'Focused studio production' },
  { value: 'Every stage', label: 'Reviewed by a working mentor' },
  { value: 'Portfolio piece', label: 'Finished work you can show' },
]

const STEPS = [
  { title: 'Brief', text: 'Start from a real, client-style brief with a clear final deliverable.' },
  { title: 'Plan and produce', text: 'Plan the work, prepare the setup and produce it in the studio.' },
  { title: 'Mentor review', text: 'Submit at each stage, get direct feedback and refine the work.' },
  { title: 'Deliver', text: 'Hand over a finished package and a portfolio case study you can show clients.' },
]

const isOpen = (p) => p.status === 'open' || p.status === 'filling'

function statusLabel(p) {
  if (isOpen(p)) {
    if (typeof p.spots_remaining === 'number' && typeof p.capacity === 'number') {
      return `Open · ${p.spots_remaining} of ${p.capacity} seats left`
    }
    return 'Open for applications'
  }
  if (p.status === 'completed') return 'Completed'
  return 'Coming soon'
}

function formatFee(fee) {
  const n = Number(fee)
  return Number.isFinite(n) && n > 0 ? `${n.toLocaleString('en-UG')} UGX` : 'Ask about fees'
}

function formatDate(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Kampala' })
}

function ProjectCard({ project }) {
  const open = isOpen(project)
  const image = DISCIPLINE_IMAGES[project.discipline] || DEFAULT_IMAGE
  const starts = formatDate(project.starts_at)
  const specs = [
    { label: 'Duration', value: project.duration || (project.duration_weeks ? `${project.duration_weeks} weeks` : '—') },
    { label: 'Starts', value: starts || 'Dates announced soon' },
    { label: 'Cohort', value: project.capacity ? `${project.capacity} people` : 'Small group' },
    { label: 'Mentorship fee', value: formatFee(project.mentorship_fee) },
  ]

  return (
    <article className="flex h-full flex-col border border-white/10 bg-white/[0.02]">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={image} alt="" loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span
          className={`absolute left-4 top-4 px-3 py-1 text-[0.65rem] uppercase tracking-[0.16em] font-sans font-semibold ${
            open ? 'bg-cinema-glow text-white' : 'bg-black/70 text-cinema-soft border border-white/15'
          }`}
        >
          {statusLabel(project)}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-cinema-gold font-sans font-semibold">
          {[project.discipline, project.level].filter(Boolean).join(' · ')}
        </p>
        <h3 className="mt-3 font-display text-2xl sm:text-3xl text-white font-light tracking-tight">{project.title}</h3>
        {project.short_description && (
          <p className="mt-4 text-base text-cinema-soft/85 font-sans leading-relaxed">{project.short_description}</p>
        )}

        <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-white/10 pt-6">
          {specs.map((s) => (
            <div key={s.label}>
              <dt className="text-[0.65rem] uppercase tracking-[0.16em] text-cinema-muted font-sans">{s.label}</dt>
              <dd className="mt-1 text-sm text-white font-sans">{s.value}</dd>
            </div>
          ))}
        </dl>

        {project.final_deliverable && (
          <div className="mt-6 border-t border-white/10 pt-6">
            <p className="text-[0.65rem] uppercase tracking-[0.16em] text-cinema-muted font-sans">What you deliver</p>
            <p className="mt-2 text-sm text-cinema-soft/85 font-sans leading-relaxed">{project.final_deliverable}</p>
          </div>
        )}

        <div className="mt-auto flex flex-col gap-3 pt-8 sm:flex-row">
          {open ? (
            <Link to={`/project-lab/${project.slug}`} className="cinema-btn-primary">
              View project and apply
            </Link>
          ) : (
            <a
              href={whatsappLink(`Hi SMA, please add me to the waitlist for the ${project.title}.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="cinema-btn-primary"
            >
              Join the waitlist
            </a>
          )}
          <a
            href={whatsappLink(`Hi SMA, I have a question about the ${project.title}.`)}
            target="_blank"
            rel="noopener noreferrer"
            className="cinema-btn-ghost"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            Ask on WhatsApp
          </a>
        </div>
      </div>
    </article>
  )
}

export default function ProjectLabPage() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    let cancelled = false
    api.get('/project-lab/projects')
      .then(({ data }) => {
        if (cancelled) return
        const list = Array.isArray(data?.projects) ? data.projects : []
        // Featured first, then open projects, then the rest.
        const rank = (p) => (p.is_featured ? 0 : isOpen(p) ? 1 : 2)
        setProjects([...list].sort((a, b) => rank(a) - rank(b)))
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const openCount = projects.filter(isOpen).length

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Project Lab | Mentored Creative Projects in Kampala | Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="SMA Project Lab: small-group, mentored projects in photography, video, design and AI. Work from a real brief and finish with portfolio-ready work in 2–6 weeks."
        />
        <link rel="canonical" href="https://synergymultimediaacademy.com/project-lab" />
        <meta property="og:title" content="Project Lab | Synergy Multimedia Academy" />
        <meta
          property="og:description"
          content="Small-group, mentored creative projects in Kampala. Work from a real brief and finish with portfolio-ready work."
        />
        <meta property="og:url" content="https://synergymultimediaacademy.com/project-lab" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="cinema-grain" aria-hidden="true" />

      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={(id) => navigate(`/#${id}`)} />

      {/* Hero */}
      <header className="relative isolate flex min-h-[70svh] items-end overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 -z-10" aria-hidden="true">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover grayscale object-[center_80%]" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/50" />
        </div>
        <div className="w-full max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 pt-40 pb-16 sm:pt-48 sm:pb-20 lg:pb-24">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Project Lab</p>
            <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-[4.5rem] text-white font-light tracking-tight leading-[1.04]">
              Learn by creating.
            </h1>
            <p className="mt-6 max-w-xl text-lg sm:text-xl text-white/85 font-sans leading-relaxed">
              Courses teach skills. Project Lab turns them into professional work: a real brief, a small group,
              a working mentor, and a finished piece for your portfolio.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href="#projects" className="cinema-btn-primary">
                {openCount > 0 ? `See open projects (${openCount})` : 'See projects'}
              </a>
              <a href="#private" className="cinema-btn-ghost bg-black/30 backdrop-blur-sm">
                Bring your own project
              </a>
            </div>
          </Reveal>
        </div>
      </header>

      {/* Facts */}
      <section aria-label="Project Lab at a glance" className="border-b border-white/10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <dl className="grid grid-cols-2 lg:grid-cols-4">
            {FACTS.map((f, i) => (
              <div
                key={f.value}
                className={`py-8 sm:py-10 ${i % 2 === 1 ? 'pl-6 border-l border-white/10' : 'pr-6'} ${
                  i >= 2 ? 'border-t border-white/10 lg:border-t-0' : ''
                } ${i === 2 ? 'lg:pl-6 lg:border-l' : ''}`}
              >
                <dd className="font-display text-2xl sm:text-3xl text-white font-light tracking-tight">{f.value}</dd>
                <dt className="mt-2 text-sm text-cinema-muted font-sans">{f.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="scroll-mt-24 py-20 sm:py-28 border-b border-white/10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Projects</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-white font-light tracking-tight">
              {openCount > 0 ? 'Open for applications' : 'Upcoming projects'}
            </h2>
            <p className="mt-4 text-base text-cinema-soft/85 font-sans leading-relaxed">
              Each project runs as a small cohort with a fixed brief, a mentor and a finished deliverable.
            </p>
          </Reveal>

          {loading ? (
            <div className="py-20 flex justify-center"><LoadingSpinner /></div>
          ) : projects.length > 0 ? (
            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              {projects.map((project, i) => (
                <Reveal key={project.id} delay={i * 60} className="h-full">
                  <ProjectCard project={project} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-12 border border-white/10 p-8 sm:p-10">
              <p className="font-display text-2xl text-white font-light">New projects are being prepared.</p>
              <p className="mt-3 text-base text-cinema-soft/85 font-sans">Get a message on WhatsApp when the next cohort opens.</p>
              <a
                href={whatsappLink('Hi SMA, please let me know when the next Project Lab cohort opens.')}
                target="_blank"
                rel="noopener noreferrer"
                className="cinema-btn-primary mt-8"
              >
                Notify me on WhatsApp
              </a>
            </div>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-28 border-b border-white/10" aria-labelledby="how-it-works">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">How it works</p>
            <h2 id="how-it-works" className="mt-3 font-display text-4xl sm:text-5xl text-white font-light tracking-tight">
              From brief to portfolio.
            </h2>
          </Reveal>
          <ol className="mt-12 grid gap-px bg-white/10 border border-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <li key={step.title} className="bg-[#0a0a0a] p-6 sm:p-8">
                <span className="font-mono text-xs text-cinema-gold tracking-[0.16em]">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="mt-4 font-display text-2xl text-white font-light">{step.title}</h3>
                <p className="mt-3 text-sm text-cinema-soft/85 font-sans leading-relaxed">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Private Project Lab */}
      <section id="private" className="scroll-mt-24 py-20 sm:py-28 border-b border-white/10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid items-center gap-10 border border-white/10 bg-white/[0.02] p-6 sm:p-10 lg:grid-cols-12 lg:p-14">
            <Reveal className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Private Project Lab</p>
              <h2 className="mt-3 font-display text-3xl sm:text-5xl text-white font-light tracking-tight">Bring your own project.</h2>
              <p className="mt-5 max-w-xl text-base sm:text-lg text-cinema-soft/85 font-sans leading-relaxed">
                Have a client brief, a personal project or a specific outcome in mind? Work one-on-one with an SMA
                mentor on a custom scope, timeline and deliverable.
              </p>
            </Reveal>
            <Reveal delay={80} className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
              <a
                href={whatsappLink('Hi SMA, I would like to discuss a private Project Lab for my project.')}
                target="_blank"
                rel="noopener noreferrer"
                className="cinema-btn-primary w-full lg:w-auto"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                Discuss it on WhatsApp
              </a>
              <Link to="/contact" className="cinema-btn-ghost w-full lg:w-auto">
                Use the contact form
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 sm:py-28 text-center">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <h2 className="font-display text-3xl sm:text-5xl text-white font-light tracking-tight">Ready to make something real?</h2>
          <p className="mt-4 text-base sm:text-lg text-cinema-soft/85 font-sans">
            Not sure which project fits your level? An advisor can help you choose.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <a href="#projects" className="cinema-btn-primary">
              See projects
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <Link to="/advisor" className="cinema-btn-ghost">Talk to an Advisor</Link>
          </div>
        </div>
      </section>

      <CinematicFooter contactEmail={CONTACT_EMAIL} closing={false} />
    </div>
  )
}
